import React from 'react';
import { Card, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { t } from '../utils/i18n';
import { getGoogleSearchUrl, getYouTubeMusicUrl } from '../utils/linkUtils';
import { formatRelativeDate, parseGenres } from '../utils/dataUtils';
import { formatRating, getRatingColor } from '../utils/ratingUtils';
import RatingStars from './RatingStars';

function typeLabel(type, language) {
  if (type === 'series') return t('cards.typeSeries', language);
  if (type === 'music') return t('cards.typeMusic', language);
  return t('cards.typeMovie', language);
}

function WatchedContentCard({
  content,
  type,
  language,
  dateLabel,
  dateStr,
  displayRating,
  onEdit,
  onDelete,
}) {
  const href =
    type === 'music'
      ? getYouTubeMusicUrl(content.title, { year: content.year, artist: content.artist })
      : getGoogleSearchUrl(content.title, { year: content.year, artist: content.artist });

  const genres = parseGenres(content.genre).slice(0, 2);
  const ratingNum = displayRating != null && displayRating !== '' ? Number(displayRating) : null;

  return (
    <Card className={`h-100 miskosas-content-card miskosas-content-card--${type}`}>
      <Card.Body>
        <div className="miskosas-content-card__top">
          <span className="miskosas-content-card__type-chip">{typeLabel(type, language)}</span>
          <span className="miskosas-content-card__tag miskosas-content-card__tag--status-watched">
            {dateLabel}
          </span>
        </div>

        <h3 className="miskosas-content-card__title">
          <a href={href} target="_blank" rel="noopener noreferrer">
            {content.title}
          </a>
        </h3>

        {content.artist ? (
          <p className="miskosas-content-card__subtitle">{content.artist}</p>
        ) : null}

        <div className="miskosas-content-card__tags">
          {genres.map((g) => (
            <span key={g} className="miskosas-content-card__tag">
              {g}
            </span>
          ))}
          {content.year ? <span className="miskosas-content-card__tag">{content.year}</span> : null}
        </div>

        {ratingNum != null && !Number.isNaN(ratingNum) ? (
          <div className="miskosas-content-card__rating-row">
            <div className={`miskosas-content-card__rating-main ${getRatingColor(ratingNum)}`}>
              {formatRating(ratingNum)}
            </div>
            <div className="miskosas-content-card__rating-details">
              <div className="d-flex align-items-center gap-1 mb-1">
                <RatingStars rating={ratingNum} size={13} />
              </div>
              <span>{t('history.yourRating', language)}</span>
            </div>
          </div>
        ) : null}

        {content.director ? (
          <p className="miskosas-content-card__meta-line">
            {content.director}
          </p>
        ) : null}

        {content.notes ? (
          <p className="miskosas-content-card__notes">{content.notes}</p>
        ) : null}

        <p className="miskosas-content-card__meta-line mb-0">
          {dateStr || '—'}
        </p>

        <div className="miskosas-content-card__footer">
          <OverlayTrigger placement="top" overlay={<Tooltip>{t('history.edit', language)}</Tooltip>}>
            <Button
              variant="outline-primary"
              size="sm"
              className="flex-grow-1"
              onClick={() => onEdit(content, type)}
            >
              <FaEdit className="me-1" />
              {t('history.edit', language)}
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{t('history.returnToRecommendations', language)}</Tooltip>}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              className="miskosas-content-card__btn-icon"
              onClick={() => onDelete(content, type)}
              aria-label={t('history.delete', language)}
            >
              <FaTrash />
            </Button>
          </OverlayTrigger>
        </div>
      </Card.Body>
    </Card>
  );
}

export default WatchedContentCard;
